/**
 * Der Gerüststand: was das Gerüst über sich selbst weiß.
 *
 * **Ohne Werkzeugleiste** (#41, #136): Sie zeigt an und bietet nichts zum
 * Handeln außer dem einen Neuladen, das am Fuß der Fläche steht und nicht auf
 * den ganzen Screen wirkt. Ein leerer Glasstreifen ist schlechter als keiner
 * (`docs/human/2026-08-27-werkzeugleiste-und-sektionen.md`).
 */

import { useCallback, useEffect, useState } from "react";
import type { ReactElement } from "react";

import { formatNumber, formatTimestamp } from "../formatting";
import { Button, Card, Chip, FormGrid, FormRow, Icon, StatusPill } from "../design-system";
import type { ScaffoldStatus, StatusApi } from "../api/client";

type State =
  | { kind: "loading" }
  | { kind: "loaded"; status: ScaffoldStatus }
  | { kind: "disconnected"; message: string };

/**
 * Was das Gerüst über sich selbst weiß, gelesen aus `GET /api/status`.
 *
 * Die Ansicht erfindet nichts: Antwortet die Gegenstelle nicht oder nicht in der
 * erwarteten Form, steht hier „nicht verbunden" und kein Wert.
 */
export function ScaffoldStatusView({ client }: { client: StatusApi }): ReactElement {
  const [state, setState] = useState<State>({ kind: "loading" });

  const load = useCallback(() => {
    let discarded = false;
    setState({ kind: "loading" });

    client.status().then(
      (status) => {
        if (!discarded) {
          setState({ kind: "loaded", status });
        }
      },
      (error: unknown) => {
        if (!discarded) {
          setState({
            kind: "disconnected",
            message: error instanceof Error ? error.message : String(error),
          });
        }
      },
    );

    return () => {
      discarded = true;
    };
  }, [client]);

  useEffect(() => load(), [load]);

  return (
    <Card
      title="Gerüst"
      // Der Kopf der Karte ist Titel links, Aktion rechts (#151). Die Kachel, die
      // hier einmal vor dem Titel stand, ist ersatzlos fort: Der V3-Kopf sieht
      // keinen Glyphen darin vor.
      action={<StateBadge state={state} />}
      style={{ width: 520, maxWidth: "100%" }}
    >
      {state.kind === "loading" ? <Notice text="Wird geladen ..." /> : null}

      {state.kind === "disconnected" ? (
        <Notice text={state.message} glyph="unplug" color="var(--ac-red)" />
      ) : null}

      {state.kind === "loaded" ? <StatusFields status={state.status} /> : null}

      <Button
        variant="outline"
        // Zwei Lücken des Baukastens stehen hier am Aufrufort. `fullWidth` führt
        // er nicht — die Breite steht deshalb im `style`, so begründet
        // `silvererp/skills#47` das Nicht-Ziel. Und einen abgetrennten Fuß führt
        // die Karte seit #151 nicht mehr: Der Knopf ist das letzte Kind des
        // Rumpfs und hält seinen Abstand zu den Feldern darüber selbst.
        style={{ width: "100%", marginTop: "var(--sp-6)" }}
        onClick={() => load()}
        icon="refresh-cw"
      >
        Neu laden
      </Button>
    </Card>
  );
}

function StateBadge({ state }: { state: State }): ReactElement {
  if (state.kind === "loading") {
    return <Chip>lädt</Chip>;
  }

  if (state.kind === "disconnected") {
    return <StatusPill tone="red">nicht verbunden</StatusPill>;
  }

  return <StatusPill tone="green">verbunden</StatusPill>;
}

function StatusFields({ status }: { status: ScaffoldStatus }): ReactElement {
  const inWalMode = status.journalMode === "wal";

  return (
    <FormGrid labelWidth="var(--grid-label-col)">
      <FormRow label="Anwendung">
        <span style={{ color: "var(--fg-1)" }}>{status.application}</span>
      </FormRow>
      <FormRow label="Schemastand">
        <span style={{ color: "var(--fg-1)", fontVariantNumeric: "tabular-nums" }}>
          {formatNumber(status.schemaVersion)}
        </span>
      </FormRow>
      <FormRow label="Journalmodus">
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          <span style={{ color: "var(--fg-1)" }}>{status.journalMode}</span>
          {inWalMode ? null : <StatusPill tone="red">nicht WAL</StatusPill>}
        </span>
      </FormRow>
      <FormRow label="Zeitpunkt">
        <span style={{ color: "var(--fg-3)", fontVariantNumeric: "tabular-nums" }}>
          {formatTimestamp(status.timestamp)}
        </span>
      </FormRow>
    </FormGrid>
  );
}

function Notice({
  text,
  glyph,
  color,
}: {
  text: string;
  glyph?: string;
  color?: string;
}): ReactElement {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 8,
        minHeight: 26,
        fontSize: "var(--fs-12)",
        color: color ?? "var(--fg-6)",
        textWrap: "pretty",
      }}
    >
      {glyph ? <Icon name={glyph} size={15} /> : null}
      {text}
    </div>
  );
}
